import { useEffect, useState } from "react";
import { Copy, Mail, UserMinus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, dateShort } from "@/lib/api";
import { SectionTitle, Field, Pill } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-lime";

const TONE = { invited: "bg-[#EDEFC4] text-ink", active: "bg-[#BEC221] text-ink", revoked: "bg-ink text-white" };
const LABEL = { invited: "Invitation sent", active: "Accepted", revoked: "Revoked" };

/** Admin sharing controls: invite, resend, revoke and preview a project's client portal. */
export default function ShareClientPortal({ projectId, portalEnabled, onToggle, onChanged }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", send_email: true });
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const load = () =>
    api.get(`/projects/${projectId}/portal-contacts`)
      .then((r) => { setData(r.data); onChanged?.(); })
      .catch(() => setData(null));

  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invitation link copied");
    } catch {
      toast.error("Copy failed — the link is shown below");
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: created } = await api.post(`/projects/${projectId}/portal-contacts`, form);
      setLastLink(created.link);
      toast.success(created.emailed ? `Invitation emailed to ${created.email}` : "Invitation link ready");
      setForm({ name: "", email: "", send_email: form.send_email });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, message) => {
    try {
      const res = await fn();
      if (res?.data?.link) setLastLink(res.data.link);
      toast.success(message);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!data) return null;

  return (
    <div className="space-y-7" data-testid="share-client-portal">
      <div>
        <SectionTitle>Client Portal Sharing</SectionTitle>
        <p className="-mt-3 max-w-2xl text-sm leading-relaxed text-stone">
          Invited contacts sign in without a password — they open their link and confirm a short
          code emailed to them. Each person sees only this project, and only what you’ve marked as
          client-visible.
        </p>
      </div>

      {!portalEnabled && (
        <div className="card-surface border-l-2 border-l-clay p-6" data-testid="portal-disabled-warning">
          <p className="text-sm text-stone">
            The portal for this project is switched off, so invitations won’t open. Turn on
            “Client portal enabled” in project settings first.
          </p>
          {onToggle && (
            <button data-testid="portal-enable-btn" onClick={onToggle}
              className="mt-4 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
              Turn the portal on
            </button>
          )}
        </div>
      )}

      <form onSubmit={invite} className="card-surface p-6" data-testid="portal-invite-form">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Contact Name">
            <input data-testid="portal-invite-name" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Avery Jordan" className={input} />
          </Field>
          <Field label="Email">
            <input data-testid="portal-invite-email" required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="avery@example.com" className={input} />
          </Field>
        </div>
        <label className="mt-5 flex items-center gap-2 text-sm text-stone">
          <input data-testid="portal-invite-send-email" type="checkbox" checked={form.send_email}
            onChange={(e) => setForm({ ...form, send_email: e.target.checked })} className="accent-lime" />
          Email the invitation now
        </label>
        <button data-testid="portal-invite-btn" type="submit" disabled={busy}
          className="mt-6 inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-50">
          <Mail className="h-4 w-4" strokeWidth={1.5} /> {busy ? "Sharing…" : "Share Client Portal"}
        </button>
      </form>

      {lastLink && (
        <div className="card-surface flex flex-wrap items-center gap-4 p-5" data-testid="portal-last-link">
          <span className="num min-w-0 flex-1 break-all text-xs text-stone">{lastLink}</span>
          <button data-testid="portal-copy-link" onClick={() => copy(lastLink)}
            className="inline-flex items-center gap-1.5 rounded-sm border border-line bg-white px-3 py-2 text-xs uppercase tracking-[0.15em] text-ink hover:border-lime">
            <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> Copy
          </button>
        </div>
      )}

      <div className="card-surface divide-y divide-line" data-testid="portal-contacts">
        {(data.contacts || []).length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-fog">No one has been invited yet.</div>
        ) : (
          data.contacts.map((c) => (
            <div key={c.id} data-testid={`portal-contact-${c.id}`}
              className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
              <div className="w-full min-w-0 sm:w-auto sm:flex-1">
                <div className="text-sm text-ink">{c.name}</div>
                <div className="mt-1 break-all text-xs text-fog">{c.email}</div>
              </div>
              <Pill tone={TONE[c.status]} testId={`portal-contact-status-${c.id}`}>{LABEL[c.status] || c.status}</Pill>              <span className="num text-xs text-fog">
                {c.accepted_at ? `Joined ${dateShort(c.accepted_at)}` : c.invited_at ? `Invited ${dateShort(c.invited_at)}` : ""}
              </span>
              <div className="flex items-center gap-3">
                <button data-testid={`portal-resend-${c.id}`}
                  onClick={() => act(() => api.post(`/portal-contacts/${c.id}/resend`), "New invitation sent")}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
                  <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} /> Resend
                </button>
                <button data-testid={`portal-revoke-${c.id}`}
                  onClick={() => act(() => api.delete(`/portal-contacts/${c.id}`), "Access revoked")}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-stone hover:text-clay">
                  <UserMinus className="h-3.5 w-3.5" strokeWidth={1.5} /> Revoke
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <p className="text-xs leading-relaxed text-fog" data-testid="portal-preview-note">
        Clients only ever see what you’ve marked as client-visible. Use “Preview as Client” above to
        check it yourself without sending anything.
      </p>
    </div>
  );
}
